import { useEffect, useRef, useState } from 'react';
import { Box, Modal } from '@mui/material';
import { PATHS } from 'AppPaths';
import { getAuth } from 'firebase/auth';
import { useAtom } from 'jotai';
import PaywallExplanationModal from 'pages/paywall/PaywallExplanationModal';
import { collectionIdAtom, limAndUsageInfoAtom } from 'store';
import { aiBotRequestTrack } from 'util/analyticsHandlers';
import { requireSub } from 'util/auth.js';
import { firebaseApp } from 'util/firebase';
import { history } from 'util/router';

import CustomSnackbar from 'components/CustomSnackbar';

import ChatBotContent from './ChatBotContent';

const auth = getAuth(firebaseApp);
const ChatBot = ({ isFullScreen, setFullScreen }) => {
  const [limAndUsageInfo] = useAtom(limAndUsageInfoAtom);
  const [collectionId] = useAtom(collectionIdAtom);

  const [currentUserMessage, setCurrentUserMessage] = useState('');
  const [collectionIdCustom, setCollectionIdCustom] = useState('');
  const [collectionOwner, setCollectionOwner] = useState('');
  const [activeTab, setActiveTab] = useState('AI chat');
  const [selectCollection, setSelectCollection] = useState('');

  const [messages, setMessages] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [notification, setNotification] = useState(null);
  const scrollBoxRef = useRef(null);

  const [saveAsCollection, setSaveAsCollection] = useState(false);
  const [isDeepResearch, setIsDeepResearch] = useState(false);

  useEffect(() => {
    if (showLimitModal) {
      history.replace(PATHS.PAYWALL);
    }
  }, [showLimitModal, history]);

  const processMessageToChatGPT = async (
    userSubmittedQuery,
    isDeepResearchRequest,
    streamEnabled = true, // Default to true as this function is for streaming
    saveCollection = false
  ) => {
    setIsLoading(true);
    // `messages` state already contains the latest user message added by handleSubmit
    const currentMessagesForHistory = messages.map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));
    const accessToken = auth.currentUser
      ? await auth.currentUser.getIdToken()
      : undefined;

    // Helper to update content of the streaming message or create a new one
    const handlePartialResponseContent = (partialContentResponse) => {
      setMessages((prevMessages) => {
        const lastMessageIndex = prevMessages.length - 1;
        if (
          lastMessageIndex >= 0 &&
          prevMessages[lastMessageIndex]?.role === 'assistant' &&
          prevMessages[lastMessageIndex]?.isStreaming
        ) {
          const updatedLastMessage = {
            ...prevMessages[lastMessageIndex],
            content:
              (prevMessages[lastMessageIndex].content || '') +
              partialContentResponse,
          };
          return [...prevMessages.slice(0, -1), updatedLastMessage];
        } else {
          // First content chunk, create new message
          return [
            ...prevMessages,
            {
              content: partialContentResponse,
              role: 'assistant',
              saved: false,
              isStreaming: true,
              isDeepResearchResponse: isDeepResearchRequest,
              ...(isDeepResearchRequest && {
                statusUpdates: [],
                lastStatus: 'Initiating research...',
              }),
            },
          ];
        }
      });
    };

    // Helper to update status for deep research messages
    const handleDeepResearchStatusUpdate = (newStatus) => {
      setMessages((prevMessages) => {
        const lastMessageIndex = prevMessages.length - 1;
        if (
          lastMessageIndex >= 0 &&
          prevMessages[lastMessageIndex]?.role === 'assistant' &&
          prevMessages[lastMessageIndex]?.isStreaming &&
          prevMessages[lastMessageIndex]?.isDeepResearchResponse
        ) {
          const lastMessage = prevMessages[lastMessageIndex];
          const existingStatuses = lastMessage.statusUpdates || [];
          if (
            existingStatuses.length === 0 ||
            existingStatuses[existingStatuses.length - 1] !== newStatus
          ) {
            return [
              ...prevMessages.slice(0, -1),
              {
                ...lastMessage,
                statusUpdates: [...existingStatuses, newStatus],
                lastStatus: newStatus,
              },
            ];
          }
          // No change if status is the same as last
          return prevMessages;
        } else if (isDeepResearchRequest) {
          // Status is the first event for deep research, create message
          return [
            ...prevMessages,
            {
              content: '', // No content yet
              role: 'assistant',
              saved: false,
              isStreaming: true,
              isDeepResearchResponse: true,
              statusUpdates: [newStatus],
              lastStatus: newStatus,
            },
          ];
        }
        return prevMessages;
      });
    };

    try {
      let endpointUrl;
      let apiRequestBodyPayload;

      if (isDeepResearchRequest) {
        endpointUrl = `${process.env.REACT_APP_PICKER_AI_API_URL}/research/stream`;
        apiRequestBodyPayload = {
          collection_id: collectionIdCustom,
          owner: collectionOwner,
          query: userSubmittedQuery,
          save: saveCollection,
        };
      } else {
        endpointUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/queryOpenAICollectionBrainStream`;
        apiRequestBodyPayload = {
          data: {
            collectionId: collectionIdCustom,
            history: currentMessagesForHistory,
            message: [{ text: userSubmittedQuery }],
            owner: collectionOwner,
            stream: streamEnabled,
          },
        };
      }
      const finalRequestBody = JSON.stringify(apiRequestBodyPayload);
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: finalRequestBody,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          'API call failed with error:',
          response.status,
          errorText
        );
        setNotification({
          message: `Error: ${errorText || response.statusText}`,
          type: 'error',
        });
        setOpenSnackbar(true);
        setIsError(true);
        setIsLoading(false);
        return;
      }

      if (streamEnabled) {
        // Handle stream response
        const reader = response.body.getReader();
        let fullResponse = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const decodedText = new TextDecoder().decode(value);
          // Handle concatenated JSON objects safely
          const sseEvents = decodedText
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter((line) => line.startsWith('data:'))
            .map((line) => line.replace('data: ', ''));

          for (const eventDataString of sseEvents) {
            try {
              const parsedData = JSON.parse(eventDataString);

              if (parsedData.report) {
                // Skip processing report events containing the full response (all concatenated deltas)
                continue;
              }

              if (parsedData.status) {
                handleDeepResearchStatusUpdate(parsedData.status);
              } else if (parsedData.content) {
                handlePartialResponseContent(parsedData.content);
              } else if (parsedData.detail && eventDataString) {
                // Handle error event from SSE stream
                setNotification({
                  message:
                    parsedData.detail +
                    (parsedData.status_code
                      ? ` (Error code: ${parsedData.status_code})`
                      : ''),
                  type: 'error',
                });
                setOpenSnackbar(true);
              }
            } catch (error) {
              console.warn('JSON parse error for SSE:', eventDataString);
            }
          }
        }
        // After stream is complete, update the last message to mark streaming as done.
        if (fullResponse.trim() !== '') {
          setMessages((prevMessages) => {
            if (prevMessages.length === 0) return prevMessages;
            const lastMessage = prevMessages[prevMessages.length - 1];
            if (lastMessage?.role === 'assistant') {
              return [
                ...prevMessages.slice(0, -1),
                { ...lastMessage, content: fullResponse, isStreaming: false },
              ];
            }
            return prevMessages;
          });
        } else {
          // If fullResponse is empty, just mark streaming as complete without changing content
          setMessages((prevMessages) => {
            if (prevMessages.length === 0) return prevMessages;
            const lastMessage = prevMessages[prevMessages.length - 1];
            if (lastMessage?.role === 'assistant') {
              return [
                ...prevMessages.slice(0, -1),
                { ...lastMessage, isStreaming: false },
              ];
            }
            return prevMessages;
          });
        }
      } else {
        // This block might be less relevant if always streaming, but kept for robustness
        throw new Error(
          'Non-streamed response handling not fully implemented for this flow.'
        );
      }
    } catch (error) {
      setIsError(true);
      setNotification({
        message: `Error: ${error.message}`,
        type: 'error',
      });
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (
    userMessageContent,
    isDeepResearch,
    saveCollection
  ) => {
    if (
      limAndUsageInfo &&
      limAndUsageInfo.entitlement === 'free' &&
      limAndUsageInfo.usedQuestions >= limAndUsageInfo.maxQuestions
    ) {
      setShowLimitModal(true);
      return;
    }

    aiBotRequestTrack();

    if (userMessageContent && userMessageContent.trim() !== '') {
      try {
        const userMessageObject = {
          content: userMessageContent,
          role: 'user',
        };
        // Add user message to state first
        setMessages((prevMessages) => [...prevMessages, userMessageObject]);
        setCurrentUserMessage(''); // Clear input field

        // Then process the request
        await processMessageToChatGPT(
          userMessageContent,
          isDeepResearch && limAndUsageInfo.entitlement === 'enterprise',
          true,
          saveCollection &&
            limAndUsageInfo.usedCollections < limAndUsageInfo.maxCollections
        );
      } catch (e) {
        const errorMessage = e.message
          .replace(/HTTP error! status: 500, /, '')
          .trim();

        setNotification({
          message: `${errorMessage} You can continue working with Picker.`,
          type: 'error',
        });

        setOpenSnackbar(true);
      }

      scrollBoxRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  const propsToPass = {
    isFullScreen,
    setFullScreen,
    currentUserMessage,
    setCurrentUserMessage,
    setCollectionOwner,
    messages,
    handleSendRequest: handleSubmit,
    isLoading,
    setIsError,
    isError,
    handleCloseError: () => setIsError(false),
    setMessages,
    scrollBoxRef,
    collectionIdCustom,
    setCollectionIdCustom,
    collectionId,
    activeTab,
    setActiveTab,
    setSelectCollection,
    selectCollection,
    saveAsCollection,
    setSaveAsCollection,
    isDeepResearch,
    setIsDeepResearch,
  };

  return (
    <>
      {showLimitModal && (
        <PaywallExplanationModal
          open={showLimitModal}
          reason="You've reached your daily limit for AI questions. Upgrade now or wait until tomorrow for a new quota."
          onClose={() => setShowLimitModal(false)}
        />
      )}
      {!isFullScreen ? (
        <Box width="96%" mx="auto">
          <ChatBotContent {...propsToPass} />
        </Box>
      ) : (
        <Modal open={isFullScreen} onClose={() => setFullScreen(false)}>
          <ChatBotContent {...propsToPass} />
        </Modal>
      )}
      {notification !== null && (
        <CustomSnackbar
          open={openSnackbar}
          onClose={() => {
            setOpenSnackbar(false);
            setNotification(null);
          }}
          notification={notification}
        />
      )}
    </>
  );
};

export default requireSub(ChatBot);
